/**
 * Génération du roadbook — EF-RBK-001, EF-RBK-008, EF-RBK-009.
 *
 * La génération est une étape autonome, lancée manuellement. Elle n'exécute
 * aucune recherche de POI et n'effectue aucun appel réseau. Elle utilise les
 * passages EF-CAL-006 déjà établis de tous les POI présents, sans les
 * recalculer.
 */

import { localDateTime, type LocalDateTime } from '../core/local-datetime';
import type {
  Passage,
  PassageComputed,
  Poi,
  PoiId,
  Results,
  SearchIntegrity,
  Stop,
  Totals,
  UsageMode,
} from '../core/model';
import type { Parameters } from '../core/parameters';
import { elevationGainAtProgression, type ComputedTrack } from '../geo/track';
import { computeFreshness } from './freshness';
import { orderedPoiLines, type PoiLineRef } from './order';
import { buildRidingTimeCurve, type RidingTimeCurve } from './riding-time';
import { computeSchedule, SLEEP_PROMPT_THRESHOLD_SECONDS } from './schedule';

/** Seuil d'avertissement de fuseau horaire de EF-CAL-002, en secondes. */
export const TIMEZONE_WARNING_THRESHOLD_SECONDS = SLEEP_PROMPT_THRESHOLD_SECONDS;

export interface ComputeInput {
  readonly track: ComputedTrack;
  readonly routeFingerprint: string;
  readonly pois: ReadonlyMap<PoiId, Poi>;
  readonly parameters: Parameters;
  readonly searchIntegrity: SearchIntegrity;
  /** Faux lorsque l'utilisateur a refusé de renseigner le sommeil. */
  readonly includeSleep: boolean;
  /** Courbe `T` conservée d'un calcul précédent, si la trace et les vitesses sont inchangées. */
  readonly curve?: RidingTimeCurve;
  readonly computedAt: LocalDateTime;
  readonly mode?: UsageMode;
}

export interface ComputeOutput {
  readonly pois: ReadonlyMap<PoiId, Poi>;
  readonly results: Results;
  readonly curve: RidingTimeCurve;
}

/** Arrondit une durée à la seconde entière. L'arrondi n'intervient qu'ici. */
const roundSeconds = (value: number): number => Math.round(value);

const roundInstant = (value: LocalDateTime): LocalDateTime => localDateTime(Math.round(value));

export function computeRoadbook(input: ComputeInput): ComputeOutput {
  const { track, pois, parameters } = input;
  const curve = input.curve ?? buildRidingTimeCurve(track, parameters.speed);

  const schedule = computeSchedule(curve, parameters.schedule, { includeSleep: input.includeSleep });

  const elevationAvailable = track.elevation.available;
  const totalGain = track.elevationGainMeters;

  // La curation s'applique : un POI exclu est retiré des calculs inter-POI.
  const lines = orderedPoiLines(pois, (poi) => !poi.userState.excluded);

  const computedByPoi = new Map<PoiId, Map<number, PassageComputed>>();
  let previous: { progressionMeters: number; arrival: LocalDateTime; gain: number | null } | null = null;

  for (const line of lines) {
    const progression = line.progressionMeters;
    const arrival = roundInstant(schedule.estimatedArrival(progression));
    const gainFromStart = elevationAvailable ? elevationGainAtProgression(track, progression) : null;
    const gainToFinish =
      elevationAvailable && totalGain !== null && gainFromStart !== null ? totalGain - gainFromStart : null;

    // Première ligne non exclue : les valeurs « depuis la ligne précédente »
    // sont comptées depuis le Départ, conformément à EF-RBK-002.
    const reference = previous ?? {
      progressionMeters: 0,
      arrival: roundInstant(schedule.startLocal),
      gain: elevationAvailable ? 0 : null,
    };

    const computed: PassageComputed = {
      distanceFromStartMeters: progression,
      distanceToFinishMeters: track.lengthMeters - progression,
      elevationGainFromStartMeters: gainFromStart,
      elevationGainToFinishMeters: gainToFinish,
      estimatedArrivalLocal: arrival,
      distanceFromPreviousMeters: progression - reference.progressionMeters,
      elevationGainFromPreviousMeters:
        gainFromStart !== null && reference.gain !== null ? gainFromStart - reference.gain : null,
      durationFromPreviousSeconds: roundSeconds(arrival - reference.arrival),
    };

    let byPassage = computedByPoi.get(line.poiId);
    if (byPassage === undefined) {
      byPassage = new Map<number, PassageComputed>();
      computedByPoi.set(line.poiId, byPassage);
    }
    byPassage.set(line.passageIndex, computed);

    previous = { progressionMeters: progression, arrival, gain: gainFromStart };
  }

  const updatedPois = new Map<PoiId, Poi>();
  for (const [id, poi] of pois) {
    const byPassage = computedByPoi.get(id);
    const passages: Passage[] = poi.passages.map((passage) => ({
      ...passage,
      computed: byPassage?.get(passage.index) ?? null,
    }));
    updatedPois.set(id, { ...poi, passages });
  }

  const stops: readonly Stop[] = schedule.stops.map((stop) => ({
    ...stop,
    startLocal: roundInstant(stop.startLocal),
    endLocal: stop.endLocal === null ? null : roundInstant(stop.endLocal),
    durationSeconds: stop.durationSeconds === null ? null : roundSeconds(stop.durationSeconds),
  }));

  const startLocal = roundInstant(schedule.startLocal);
  const finishLocal = roundInstant(schedule.finishLocal);
  const totalSeconds = finishLocal - startLocal;

  const totals: Totals = {
    distanceMeters: track.lengthMeters,
    elevationGainMeters: elevationAvailable ? totalGain : null,
    ridingSeconds: roundSeconds(schedule.ridingSeconds),
    pauseSeconds: roundSeconds(schedule.pauseSeconds),
    sleepSeconds: roundSeconds(schedule.sleepSeconds),
    totalSeconds,
    startLocal,
    finishLocal,
    averageSpeedKmh: totalSeconds > 0 ? track.lengthMeters / 1000 / (totalSeconds / 3600) : 0,
  };

  const freshness = computeFreshness({
    routeFingerprint: input.routeFingerprint,
    pois: updatedPois,
    parameters: { speed: parameters.speed, schedule: parameters.schedule },
  });

  const results: Results = {
    computedAt: input.computedAt,
    mode: input.mode ?? 'analysis',
    totals,
    stops,
    flags: {
      sleepSkippedByUser: parameters.schedule.sleep.declinedByUser || !input.includeSleep,
      elevationUnavailable: !elevationAvailable,
      timezoneWarning: totalSeconds > TIMEZONE_WARNING_THRESHOLD_SECONDS,
      needsRecalculation: false,
    },
    freshness,
    searchIntegrity: input.searchIntegrity,
    raceState: null,
  };

  return { pois: updatedPois, results, curve };
}

/**
 * Vrai lorsque la courbe `T` doit être reconstruite. Elle possède une
 * dépendance plus étroite que l'empreinte de fraîcheur : la trace de calcul et
 * les quatre vitesses de EF-CAL-001, et elles seules (EF-CAL-005).
 */
export function ridingCurveNeedsRebuild(
  previousSpeed: Parameters['speed'] | null,
  currentSpeed: Parameters['speed'],
  trackChanged: boolean,
): boolean {
  if (trackChanged || previousSpeed === null) return true;
  return (
    previousSpeed.referenceKmh !== currentSpeed.referenceKmh ||
    previousSpeed.uphill5Kmh !== currentSpeed.uphill5Kmh ||
    previousSpeed.maxKmh !== currentSpeed.maxKmh ||
    previousSpeed.minKmh !== currentSpeed.minKmh
  );
}

/** Lignes de POI ordonnées, curation appliquée. Réexporté pour l'interface. */
export function computedLineOrder(pois: ReadonlyMap<PoiId, Poi>): readonly PoiLineRef[] {
  return orderedPoiLines(pois, (poi) => !poi.userState.excluded);
}
